import type { DispatchingStatus } from '@backoffice-frontend/graphql'
import { getOddIconSrc } from '@backoffice-frontend/overwatch-map-common'
import {
  CopyIcon,
  EyeIcon,
  FileArchiveIcon,
  LinkHorizontalIcon,
  MoreVerticalIcon,
  PenIcon,
  RouteIcon,
  TagIcon,
  XIcon,
} from '@moia-dev/pace-icons'
import {
  ActionItem,
  ActionItemDivider,
  ActionMenu,
  Badge,
  Button,
  Heading,
  IconButton,
  LoadingIndicator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useActionMenu,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AllWaypointsModeIndicator } from '../AllWaypointsModeIndicator'
import type { ODDMapElement, Waypoint } from '../TripEditor/types'
import {
  aggregateOddElements,
  ellipseOrbit,
  getDispatchStatusDisplay,
  getOddTypeLabel,
} from '../utils'
import type { SavedTrip } from './SavedTripCard'

const TRIP_TYPE_LABELS: Record<string, string> = {
  TRIP_TYPE_GOODS: 'Goods',
  TRIP_TYPE_INTERNAL: 'V&V',
  TRIP_TYPE_VNV: 'V&V',
  TRIP_TYPE_PASSENGER: 'Passenger',
}

const formatDistanceMiles = (meters: number) => {
  const miles = meters * 0.000621371
  if (miles < 0.1) return '< 0.1 mi'
  return `${Math.round(miles * 10) / 10} mi`
}

const formatLegDistance = (meters: number) => {
  const miles = meters * 0.000621371
  if (miles < 0.1) return '< 0.1 mi'
  return `${Math.round(miles * 10) / 10} mi`
}

const formatLegTime = (seconds: number) => {
  const mins = Math.ceil(seconds / 60)
  return `${mins} min`
}

const formatModifiedDate = (ms: number): string => {
  const d = new Date(ms)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  return `${month}/${day}/${year}`
}

const styles = {
  statsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
} as const

export type RouteLegInfo = {
  distanceMeters: number
  etaSeconds: number
}

type SavedRouteDetailsPanelProps = {
  trip: SavedTrip
  waypoints: Waypoint[]
  legData?: RouteLegInfo[]
  oddElements?: ODDMapElement[]
  fetchingOdds?: boolean
  loading?: boolean
  archiving?: boolean
  assigning?: boolean
  onClose: () => void
  onEdit: (trip: SavedTrip) => void
  onDuplicate: (trip: SavedTrip) => void
  onRename: (trip: SavedTrip) => void
  onManageVtr: (trip: SavedTrip) => void
  onArchive: (trip: SavedTrip) => void
  onAssign: (trip: SavedTrip) => void
  /** Copy a shareable deep link to this route. Omitted in the assign-trip modal. */
  onCopyLink?: (trip: SavedTrip) => void
  /** Live dispatch status while assigning; drives the status pill. */
  dispatchStatus?: DispatchingStatus | null
  /** Hide the 3-dots action menu entirely (assign-trip modal flow). */
  hideActions?: boolean
  /** Lives on the trip details query, not on the `trip` list entry. */
  allWaypointsMode?: boolean
}

// Match the exact colors used by RouteMap.tsx for map markers.
const VALIDATED_COLOR = '#0532ff'
const UNVALIDATED_COLOR = '#E87722'

const LABEL_HEIGHT = 36

const ReadonlyWaypointRow = ({
  waypoint,
  index,
  isLast,
  legInfo,
}: {
  waypoint: Waypoint
  index: number
  isLast: boolean
  legInfo?: RouteLegInfo
}) => {
  const markerColor = waypoint.validated ? VALIDATED_COLOR : UNVALIDATED_COLOR
  // An "odd" waypoint carries an ODD element (stop sign, traffic light, …). Show
  // its icon in a pill exactly like the map marker and the editor's waypoint
  // list, instead of the plain numbered circle. Truthiness of `oddElementType`
  // is the same gate the map (RouteMap) and editor (WaypointList) use.
  const hasOdd = !!waypoint.oddElementType

  return (
    <div css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Label row: circle + text, both the same fixed height */}
      <div
        css={{ display: 'flex', alignItems: 'center', height: LABEL_HEIGHT }}
      >
        <div
          css={{
            width: 32,
            display: 'flex',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {hasOdd ? (
            <div
              css={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                borderRadius: 16,
                padding: '0 5px 0 3px',
                gap: 4,
                height: 28,
                backgroundColor: markerColor,
                border: '2px solid white',
                outline: '1px solid rgba(0,0,0,0.18)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.22)',
                overflow: 'visible',
              }}
            >
              <img
                src={getOddIconSrc(waypoint.oddElementType!)}
                width={20}
                height={20}
                alt=""
                css={{ display: 'block', flexShrink: 0 }}
              />
              <span
                css={{
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {index + 1}
              </span>
              {waypoint.heading != null &&
                (() => {
                  const pillRx =
                    28 + Math.max(0, String(index + 1).length - 1) * 4
                  const { dx, dy } = ellipseOrbit(pillRx, 22, waypoint.heading)
                  return (
                    <svg
                      width={12}
                      height={9}
                      viewBox="0 0 14 10"
                      fill="none"
                      css={{
                        position: 'absolute',
                        left: `calc(50% + ${dx - 6}px)`,
                        top: `calc(50% + ${dy - 4.5}px)`,
                        pointerEvents: 'none',
                        zIndex: 3,
                        transform: `rotate(${waypoint.heading}deg)`,
                      }}
                    >
                      <path
                        d="M7 1L13 9H1Z"
                        fill="white"
                        stroke={markerColor}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                })()}
            </div>
          ) : (
            <div
              css={{
                position: 'relative',
                borderRadius: '50%',
                width: 26,
                height: 26,
                backgroundColor: markerColor,
                border: '2px solid white',
                outline: '1px solid rgba(0,0,0,0.18)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.22)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
              }}
            >
              <span css={{ color: 'white', fontSize: 11, fontWeight: 600 }}>
                {index + 1}
              </span>
              {waypoint.heading != null &&
                (() => {
                  const { dx, dy } = ellipseOrbit(21, 21, waypoint.heading)
                  return (
                    <svg
                      width={12}
                      height={9}
                      viewBox="0 0 14 10"
                      fill="none"
                      css={{
                        position: 'absolute',
                        left: `calc(50% + ${dx - 6}px)`,
                        top: `calc(50% + ${dy - 4.5}px)`,
                        pointerEvents: 'none',
                        zIndex: 3,
                        transform: `rotate(${waypoint.heading}deg)`,
                      }}
                    >
                      <path
                        d="M7 1L13 9H1Z"
                        fill="white"
                        stroke={markerColor}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                })()}
            </div>
          )}
        </div>
        {/* The odd pill is wider than the 32px marker column, so nudge the
            address text right for odd rows to keep the same visual gap. */}
        <div css={{ flex: 1, minWidth: 0, paddingLeft: hasOdd ? 15 : 10 }}>
          <Text
            size="sm"
            css={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {waypoint.address || `Waypoint ${index + 1}`}
          </Text>
        </div>
      </div>

      {/* Connector: dashed line + leg distance/time between waypoints */}
      {!isLast && (
        <div css={{ display: 'flex', alignItems: 'stretch' }}>
          <div
            css={{
              width: 32,
              display: 'flex',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <div
              css={theme => ({
                borderLeft: `2px dashed ${theme.semantic.ColorAccessoryReduced}`,
                height: '100%',
                minHeight: legInfo ? 32 : 20,
              })}
            />
          </div>
          {legInfo && (
            <div
              css={theme => ({
                paddingLeft: 10,
                display: 'flex',
                alignItems: 'center',
                gap: theme.space.Space2,
              })}
            >
              <Text size="sm" color="reduced">
                {formatLegDistance(legInfo.distanceMeters)}
              </Text>
              <Text size="sm" color="reduced">
                ·
              </Text>
              <Text size="sm" color="reduced">
                {formatLegTime(legInfo.etaSeconds)}
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const SavedRouteDetailsPanel = ({
  trip,
  waypoints,
  legData,
  oddElements = [],
  fetchingOdds = false,
  loading = false,
  archiving = false,
  assigning = false,
  onClose,
  onEdit,
  onDuplicate,
  onRename,
  onManageVtr,
  onArchive,
  onAssign,
  onCopyLink,
  dispatchStatus,
  hideActions,
  allWaypointsMode = false,
}: SavedRouteDetailsPanelProps) => {
  const { t } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { getReferenceProps, ...menuProps } = useActionMenu({
    isOpen: isMenuOpen,
    setIsOpen: setIsMenuOpen,
  })

  const tripTypeLabel = TRIP_TYPE_LABELS[trip.tripType] ?? trip.tripType
  const dispatchDisplay = getDispatchStatusDisplay(dispatchStatus)
  const sortedOdds = aggregateOddElements(oddElements)
  const totalDistanceMeters =
    legData && legData.length > 0
      ? legData.reduce((sum, leg) => sum + leg.distanceMeters, 0)
      : trip.distance

  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // White surface (matches the sidebar and map cards); ColorBackgroundDefault
        // is the off-white page background, not white.
        backgroundColor: theme.semantic.ColorSurfaceDefault,
      })}
    >
      {/* Header */}
      <div
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
          padding: `${theme.space.Space3} ${theme.space.Space3} ${theme.space.Space3} ${theme.space.Space4}`,
          borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          flexShrink: 0,
        })}
      >
        <div css={{ flex: 1, minWidth: 0 }}>
          {/* CSS-only truncation + full-name tooltip (same pattern as
              SavedTripCard): the Heading is the direct TooltipTrigger child with
              `display: inline-block` + `maxWidth: 100%` so it clips, and delay=0
              removes pace-web's lingering close delay. */}
          <div css={{ minWidth: 0, overflow: 'hidden' }}>
            <Tooltip placement="top" delay={0}>
              <TooltipTrigger>
                <Heading
                  size="sm"
                  css={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                  }}
                >
                  {trip.name}
                </Heading>
              </TooltipTrigger>
              <TooltipContent css={{ overflowWrap: 'anywhere' }}>
                {trip.name}
              </TooltipContent>
            </Tooltip>
          </div>
          <Text size="sm" color="reduced">
            <span css={{ fontWeight: 'bold' }}>Modified:</span>
            {` ${formatModifiedDate(trip.updatedMs)}`}
          </Text>
        </div>
        {!hideActions && (
          <div css={{ position: 'relative', flexShrink: 0 }}>
            <IconButton
              aria-label="Route actions"
              size="compact"
              variant="plain"
              icon={<MoreVerticalIcon />}
              {...getReferenceProps()}
              ref={menuProps.refs.setReference}
            />
            <ActionMenu {...menuProps}>
              <ActionItem
                label="Duplicate"
                leading={<CopyIcon />}
                onClick={() => {
                  setIsMenuOpen(false)
                  onDuplicate(trip)
                }}
              />
              <ActionItem
                label="Rename"
                leading={<PenIcon />}
                onClick={() => {
                  setIsMenuOpen(false)
                  onRename(trip)
                }}
              />
              {onCopyLink && (
                <ActionItem
                  label="Copy link"
                  leading={<LinkHorizontalIcon />}
                  onClick={() => {
                    setIsMenuOpen(false)
                    onCopyLink(trip)
                  }}
                />
              )}
              <ActionItem
                label="Edit"
                leading={<PenIcon />}
                onClick={() => {
                  setIsMenuOpen(false)
                  onEdit(trip)
                }}
              />
              <ActionItem
                label="VTR Selection"
                leading={<TagIcon />}
                onClick={() => {
                  setIsMenuOpen(false)
                  onManageVtr(trip)
                }}
              />
              <ActionItemDivider />
              <ActionItem
                label={trip.isArchived ? 'Restore' : 'Archive'}
                leading={
                  archiving ? (
                    <LoadingIndicator size={16} />
                  ) : (
                    <FileArchiveIcon />
                  )
                }
                disabled={archiving}
                onClick={() => {
                  onArchive(trip)
                }}
              />
            </ActionMenu>
          </div>
        )}
        <IconButton
          aria-label="Close details"
          size="compact"
          variant="plain"
          icon={<XIcon />}
          onClick={onClose}
          css={{ flexShrink: 0 }}
        />
      </div>

      {/* Scrollable content */}
      <div
        css={theme => ({
          flex: 1,
          overflowY: 'auto',
          padding: theme.space.Space4,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space4,
        })}
      >
        {loading ? (
          <div
            css={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 120,
            }}
          >
            <LoadingIndicator size={32} />
          </div>
        ) : (
          <>
            {/* Type + Viewing badge row — mirrors editor's type + status row */}
            <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Heading size="md" css={{ fontSize: 'calc(1em + 4px)' }}>
                {tripTypeLabel}
              </Heading>
              <Badge label="Viewing" variant="neutral" icon={<EyeIcon />} />
            </div>

            {allWaypointsMode && <AllWaypointsModeIndicator />}

            {/* Waypoint list — no gap between rows so the dashed connector joins circles */}
            {waypoints.length === 0 ? (
              <Text size="sm" color="reduced">
                No waypoints
              </Text>
            ) : (
              <div
                css={theme => ({
                  display: 'flex',
                  flexDirection: 'column',
                  paddingLeft: theme.space.Space2,
                })}
              >
                {waypoints.map((wp, idx) => (
                  <ReadonlyWaypointRow
                    key={wp.id}
                    waypoint={wp}
                    index={idx}
                    isLast={idx === waypoints.length - 1}
                    legInfo={legData?.[idx]}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats section */}
      <div
        css={theme => ({
          flexShrink: 0,
          borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
        })}
      >
        <div
          css={theme => ({
            padding: `${theme.space.Space3} ${theme.space.Space4}`,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space1,
          })}
        >
          <div css={styles.statsRow}>
            <Text size="sm" weight="bold">
              Distance
            </Text>
            <Text size="sm" weight="bold">
              {totalDistanceMeters
                ? formatDistanceMiles(totalDistanceMeters)
                : '—'}
            </Text>
          </div>

          <div css={styles.statsRow}>
            <Text size="sm" weight="bold">
              Stops
            </Text>
            <Text size="sm" weight="bold">
              {waypoints.length}
            </Text>
          </div>

          {fetchingOdds && oddElements.length === 0 && (
            <div css={styles.statsRow}>
              <Text size="sm" color="reduced">
                Fetching ODD elements…
              </Text>
              <LoadingIndicator size={16} />
            </div>
          )}

          {sortedOdds.map(([type, count]) => (
            <div key={type} css={styles.statsRow}>
              <Text size="sm" color="reduced">
                {getOddTypeLabel(type, t)}
              </Text>
              <Text size="sm" color="reduced">
                {count}
              </Text>
            </div>
          ))}
        </div>

        <div
          css={theme => ({
            padding: `0 ${theme.space.Space4} ${theme.space.Space4}`,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space2,
          })}
        >
          {dispatchDisplay && (
            <div css={{ display: 'flex', justifyContent: 'center' }}>
              <Badge
                label={dispatchDisplay.label}
                variant={dispatchDisplay.variant}
                icon={
                  dispatchDisplay.inProgress ? (
                    <LoadingIndicator size={16} />
                  ) : undefined
                }
              />
            </div>
          )}
          <Button
            label={assigning ? 'Assigning…' : 'Assign to Vehicle'}
            variant="secondary"
            size="compact"
            disabled={assigning}
            icon={
              assigning ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  css={{
                    display: 'block',
                    animation: 'savedRoute-assign-spin 0.8s linear infinite',
                    '@keyframes savedRoute-assign-spin': {
                      to: { transform: 'rotate(360deg)' },
                    },
                  }}
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="28"
                    strokeDashoffset="10"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <RouteIcon />
              )
            }
            onClick={() => onAssign(trip)}
            css={{ width: '100%' }}
          />
        </div>
      </div>
    </div>
  )
}
